import { Center } from '@mantine/core';
import { useContext, useEffect } from 'react';
import { Navigate } from 'react-router';
import { DEFAULT_ROUTE } from '../../../constants';
import { UserContext } from '../../../contexts';
import {
  getValidatedOAuthNextTarget,
  isBackendOAuthRedirectLooping,
  markBackendOAuthRedirectAttempt,
  useDocumentTitle,
} from '../../../helpers/general-helpers';
import { isValidAccessToken } from '../../../helpers/storage/storage';
import { usePostLoginRedirect } from '../../../hooks/usePostLoginRedirect';
import { useAuthStore } from '../../../store/auth-store/auth-store';
import { SignInStep } from '../../../store/auth-store/auth-store.type';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { Loader } from '../../../ui-library/loader/loader';
import { Login } from './login/login';
import { SelectOrganization } from './select-organization/select-organization';

export const SignIn = () => {
  useDocumentTitle('Sign In - Bloomfilter');

  const step = useAuthStore((state) => state.signInStep);
  const organization = useGlobalStore((state) => state.organization);
  const { user, query } = useContext(UserContext);
  const isAuthenticated = isValidAccessToken();

  const shouldRedirectToApp = Boolean(isAuthenticated && organization && step !== SignInStep.SelectOrganization);

  // A validated oauth_next in the URL means an MCP OAuth continuation is owed.
  const oauthNextTarget = shouldRedirectToApp ? getValidatedOAuthNextTarget() : null;

  // One-shot loop guard: a backend that cannot establish a session bounces the
  // user straight back to `/sign-in?oauth_next=…`. Without this we would forward
  // again and loop indefinitely, so if a forward was just attempted we stop and
  // fall through to the app instead.
  const oauthLooping = Boolean(oauthNextTarget) && isBackendOAuthRedirectLooping();

  // Gate the continuation on the FRESHLY-fetched user, never the persisted org.
  // The backend does NOT enforce T&C / org membership on /o/authorize/ (only
  // is_active), so this FE check stops the app from AUTO-forwarding a
  // T&C-reversioned or org-revoked user into an MCP token grant. It is
  // defense-in-depth, NOT a security boundary — a caller holding a valid access
  // token can hit /o/authorize/ directly, so the backend must own enforcement.
  //
  // `user` loads async: UserProvider copies the query result into `user` one
  // render AFTER the query resolves, so settle on `user != null` — never
  // `query.isFetched` alone, which flips a render early (while `query.data` is
  // already truthy) and would let a stored deep link win the race. We ALSO
  // settle when the fetch errored, or when it COMPLETED with a falsy body
  // (`isFetched` and no `data` → UserProvider never copies a user, so `user`
  // would stay null forever). `query.data` is the only signal that distinguishes
  // the benign one-render copy lag (data present → keep waiting) from a
  // genuinely userless response (fall through). Every non-loaded outcome falls
  // through — never mints a token.
  const userSettled = user != null || query?.isError === true || (query?.isFetched === true && !query?.data);
  // Offline: React Query parks the fetch at `paused` and never settles it, so
  // end the wait here rather than blank indefinitely.
  const userFetchPaused = query?.fetchStatus === 'paused';
  const isFullyOnboarded = Boolean(user?.terms_and_conditions_accepted && (user?.organizations?.length ?? 0) >= 1);
  const continueOAuth = Boolean(oauthNextTarget) && isFullyOnboarded && !oauthLooping;
  // Wait for the fresh user before deciding — except when offline or when a
  // forward is already looping; those fall through instead. The wait is bounded
  // by the /users/me/ fetch, which settles (or errors) within its request timeout.
  const awaitingUserForOAuth = Boolean(oauthNextTarget) && !oauthLooping && !userSettled && !userFetchPaused;

  // Suppress the stored-deep-link redirect whenever an MCP OAuth continuation is
  // owed (redirecting now, or waiting for the user to settle), so the callback
  // wins over a stale bloomfilter_post_login_redirect.
  const useRedirect = usePostLoginRedirect(shouldRedirectToApp && !continueOAuth && !awaitingUserForOAuth);

  // Fire the external navigation from an effect — render must stay pure
  // (StrictMode double-invokes it; a transition render may be discarded). Stamp
  // the attempt first so a bounce-back is detected as a loop, not retried.
  useEffect(() => {
    if (continueOAuth && oauthNextTarget) {
      markBackendOAuthRedirectAttempt();
      window.location.assign(oauthNextTarget);
    }
  }, [continueOAuth, oauthNextTarget]);

  if (shouldRedirectToApp) {
    // Redirecting to the backend OAuth callback (performed by the effect above).
    if (continueOAuth) {
      return null;
    }
    // Show a loader while the fresh user settles, so the bounded wait gives
    // feedback instead of a blank screen.
    if (awaitingUserForOAuth) {
      return (
        <Center h="100vh">
          <Loader />
        </Center>
      );
    }
    // Return null while the hook processes a stored deep link; fall through to
    // the default route if none was found.
    if (useRedirect) {
      return null;
    }

    return <Navigate to={DEFAULT_ROUTE} replace />;
  }

  switch (step) {
    case SignInStep.Login:
      return <Login />;
    case SignInStep.SelectOrganization:
      return <SelectOrganization />;
    default:
      return null;
  }
};
